import React, { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Switch, TextField, Typography } from "@material-ui/core";
import { AnnouncementOutlined, Close, DeleteOutline, ImageOutlined } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import CrmTheme from "../../pages/CrmRelationship/theme";
import useStyles from "../../pages/Annoucements/styles";

const initialValues = () => ({ title: "", text: "", priority: 3, status: true });

function AnnouncementForm({ open, onClose, announcementId, reload }) {
  const classes = useStyles();
  const [values, setValues] = useState(initialValues);
  const [existingMedia, setExistingMedia] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [removeMedia, setRemoveMedia] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const savedId = useRef(announcementId);
  const fileInput = useRef(null);
  useEffect(() => {
    if (!open) return;
    let current = true;
    savedId.current = announcementId;
    setValues(initialValues()); setErrors({}); setError(""); setAttachment(null); setExistingMedia(null); setRemoveMedia(false); setLoadError(false);
    if (!announcementId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/announcements/${announcementId}`).then(({ data }) => {
      if (!current) return;
      setValues({ title: data.title || "", text: data.text || "", priority: Number(data.priority) || 3, status: Boolean(data.status) });
      setExistingMedia(data.mediaPath ? { url: data.mediaPath, name: data.mediaName } : null);
    }).catch(() => { if (current) setLoadError(true); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [open, announcementId, revision]);
  useEffect(() => {
    if (!attachment) { setPreviewUrl(""); return; }
    const url = URL.createObjectURL(attachment);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);
  const close = () => { if (!lock.current) onClose(); };
  const change = (field, value) => { setValues(current => ({ ...current, [field]: value })); setErrors(current => ({ ...current, [field]: "" })); };
  const chooseFile = event => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setErrors(current => ({ ...current, attachment: "Selecione uma imagem PNG ou JPEG de até 5 MB." })); return;
    }
    setErrors(current => ({ ...current, attachment: "" })); setAttachment(file); setRemoveMedia(false);
  };
  const clearMedia = () => { setAttachment(null); setRemoveMedia(true); setErrors(current => ({ ...current, attachment: "" })); };
  const save = async event => {
    event.preventDefault();
    if (lock.current || loading || loadError) return;
    const payload = { ...values, title: values.title.trim(), text: values.text.trim() };
    const nextErrors = {};
    if (!payload.title || payload.title.length > 255) nextErrors.title = "Informe um título de até 255 caracteres.";
    if (!payload.text) nextErrors.text = "Informe a mensagem.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    lock.current = true; setBusy(true); setError("");
    let recordSaved = false;
    let completed = false;
    try {
      if (savedId.current) await api.put(`/announcements/${savedId.current}`, payload);
      else {
        const { data } = await api.post("/announcements", payload);
        savedId.current = data.id;
      }
      recordSaved = true;
      if (attachment) {
        const formData = new FormData(); formData.append("file", attachment);
        await api.post(`/announcements/${savedId.current}/media-upload`, formData);
      } else if (removeMedia && existingMedia) await api.delete(`/announcements/${savedId.current}/media-upload`);
      completed = true;
      toast.success("Informativo salvo.");
    } catch (err) {
      const message = err.response?.data?.error;
      setError(recordSaved ? "O texto foi salvo, mas a imagem não foi atualizada. Tente salvar novamente." : typeof message === "string" && !message.startsWith("ERR_") ? message : "Não foi possível salvar o informativo. Tente novamente.");
    } finally {
      lock.current = false; setBusy(false);
      if (recordSaved && reload) reload();
    }
    if (completed) onClose();
  };
  const mediaUrl = attachment ? previewUrl : !removeMedia ? existingMedia?.url : null;
  const hasMedia = Boolean(attachment || (!removeMedia && existingMedia));
  return <Dialog open={open} onClose={close} fullWidth maxWidth="sm" classes={{ paper: classes.dialog }} aria-labelledby="announcement-form-title">
    <DialogTitle id="announcement-form-title" className={classes.dialogTitle}><AnnouncementOutlined />{announcementId ? "Editar informativo" : "Novo informativo"}<IconButton className={classes.close} aria-label="Fechar informativo" disabled={busy} onClick={close}><Close /></IconButton></DialogTitle>
    <form noValidate onSubmit={save} className={classes.form}>
      <DialogContent className={classes.content}>
        {loading ? <div className={classes.loadState}><CircularProgress size={28} aria-label="Carregando informativo" /></div> : loadError ? <div className={classes.loadState} role="alert"><Typography>Não foi possível carregar o informativo.</Typography><Button onClick={() => setRevision(value => value + 1)}>Tentar novamente</Button></div> : <>
          <TextField id="announcement-title" name="title" label="Título" autoFocus fullWidth variant="outlined" size="small" value={values.title} onChange={event => change("title", event.target.value)} error={Boolean(errors.title)} helperText={errors.title} disabled={busy} inputProps={{ maxLength: 255 }} />
          <TextField id="announcement-message" name="text" label="Mensagem" fullWidth variant="outlined" size="small" multiline rows={5} value={values.text} onChange={event => change("text", event.target.value)} error={Boolean(errors.text)} helperText={errors.text} disabled={busy} />
          <TextField id="announcement-priority" label="Prioridade" fullWidth variant="outlined" size="small" select value={values.priority} onChange={event => change("priority", Number(event.target.value))} disabled={busy}><MenuItem value={1}>Alta</MenuItem><MenuItem value={2}>Média</MenuItem><MenuItem value={3}>Baixa</MenuItem></TextField>
          <div className={classes.switchRow}><div><Typography component="label" htmlFor="announcement-status">Informativo ativo</Typography><div className={classes.helper}>Visível nos avisos de todas as empresas.</div></div><Switch id="announcement-status" checked={values.status} color="primary" onChange={event => change("status", event.target.checked)} disabled={busy} inputProps={{ "aria-label": "Informativo ativo" }} /></div>
          <div className={classes.attachment}>
            <input hidden type="file" accept="image/png,image/jpeg" aria-label="Imagem do informativo" ref={fileInput} onChange={chooseFile} disabled={busy} />
            {hasMedia ? <><div className={classes.attachmentHeader}><ImageOutlined /><span>{attachment ? attachment.name : existingMedia.name || "Imagem anexada"}</span><IconButton className={classes.action} aria-label="Remover imagem" disabled={busy} onClick={clearMedia}><DeleteOutline fontSize="small" /></IconButton></div>{mediaUrl && <img src={mediaUrl} alt="Prévia da imagem do informativo" className={classes.image} />}<Button startIcon={<ImageOutlined />} disabled={busy} onClick={() => fileInput.current.click()}>Trocar imagem</Button></> : <><Button variant="outlined" startIcon={<ImageOutlined />} disabled={busy} onClick={() => fileInput.current.click()}>Adicionar imagem</Button><Typography className={classes.helper}>Opcional · PNG ou JPEG · até 5 MB</Typography></>}
            {errors.attachment && <Typography color="error" role="alert">{errors.attachment}</Typography>}
          </div>
        </>}
        {error && <Typography color="error" role="alert">{error}</Typography>}
      </DialogContent>
      <DialogActions className={classes.dialogActions}><Button variant="outlined" disabled={busy} onClick={close}>Cancelar</Button><Button type="submit" variant="contained" color="primary" disabled={busy || loading || loadError}>{busy ? "Salvando…" : "Salvar informativo"}</Button></DialogActions>
    </form>
  </Dialog>;
}

export default function AnnouncementModal(props) { return <CrmTheme><AnnouncementForm {...props} /></CrmTheme>; }
